import { useAppConfigContext } from '@/context/AppConfig';
import { Button, Icons, Modal, Stack, Text, styled } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import { HorizontalLine } from '../../components';

const ConfirmModalContainer = styled(Modal)(({ theme }) => ({
  overflowX: 'hidden',
  '.MuiDialogTitle-root': {
    display: 'flex',
    padding: `0 ${theme.spacing(3)}`,
    margin: `${theme.spacing(2.5)} 0 0 0`,
  },
  '.confirm-modal': {
    overflowX: 'hidden',
    maxWidth: 'none',
  },
  '.MuiDialogContent-root': {
    width: 'auto',
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: '420px',
    [theme.breakpoints.up('sm')]: {
      width: '585px',
    },
  },
}));

const UpdatedValue = styled(Stack)(({ theme }) => ({
  width: '33%',
  borderRadius: '4px',
  background: theme.palette.background.default,
  alignItems: 'center',
  padding: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    width: '15.5%',
  },
}));

const OriginalValue = styled(Stack)(({ theme }) => ({
  width: '33%',
  borderRadius: '4px',
  alignItems: 'center',
  padding: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    width: '15.5%',
  },
}));

const ValueTitle = styled(Stack)(({ theme }) => ({
  width: '25%',
  alignItems: 'center',
  paddingRight: theme.spacing(0.375),
  [theme.breakpoints.up('sm')]: {
    width: '16%',
  },
}));

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  openStatusModal: () => void;
  openEditModal: () => void;
  originalBeds: string;
  originalBaths: string;
  originalSqft: string;
  beds: string;
  baths: string;
  sqft: string;
}

export function ConfirmModal({
  open,
  onClose,
  openEditModal,
  openStatusModal,
  originalBeds,
  originalBaths,
  originalSqft,
  beds,
  baths,
  sqft,
}: ConfirmModalProps) {
  const { trackEvent } = useAppConfigContext();
  const { isMobile } = useScreenSize();

  const handleConfirmChanges = () => {
    trackEvent(
      'edit property details confirm changes',
      'engagement click',
      'edit property details flow'
    );
    openStatusModal();
  };

  return (
    <ConfirmModalContainer
      open={open}
      onClose={onClose}
      classes={{ paper: 'confirm-modal' }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
        }}
      >
        <Text variant="h2" mb={0}>
          Confirm Changes
        </Text>
        <Icons.Close
          onClick={onClose}
          sx={{
            cursor: 'pointer',
          }}
        />
      </Stack>
      <HorizontalLine sx={{ marginBottom: 0 }} />
      <Text variant="body1" padding="20px 0">
        Please confirm the following update.
      </Text>
      <Stack padding="14px 8px 0 8px">
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            mb: 0.75,
          }}
        >
          <ValueTitle>
            <Text variant="caption" fontSize="0.75rem">
              Original
            </Text>
          </ValueTitle>
          <ValueTitle>
            <Text variant="caption" fontSize="0.75rem">
              Updated
            </Text>
          </ValueTitle>
        </Stack>
        <Stack sx={{ flexDirection: 'row', alignItems: 'flex-end' }}>
          <Stack sx={{ width: '70%', padding: (theme) => theme.spacing(1) }}>
            <Text>Beds:</Text>
          </Stack>
          <OriginalValue>
            <Text margin={0}>{originalBeds}</Text>
          </OriginalValue>
          <UpdatedValue>
            <Text variant="h5" margin={0} lineHeight="1.5">
              {beds}
            </Text>
          </UpdatedValue>
        </Stack>
        <HorizontalLine
          sx={{ margin: (theme) => `${theme.spacing(1.5)} auto` }}
        />
        <Stack sx={{ flexDirection: 'row', alignItems: 'flex-end' }}>
          <Stack sx={{ width: '70%', padding: (theme) => theme.spacing(1) }}>
            <Text>Baths:</Text>
          </Stack>
          <OriginalValue>
            <Text margin={0}>{originalBaths}</Text>
          </OriginalValue>
          <UpdatedValue>
            <Text variant="h5" margin={0} lineHeight="1.5">
              {baths}
            </Text>
          </UpdatedValue>
        </Stack>
        <HorizontalLine
          sx={{ margin: (theme) => `${theme.spacing(1.5)} auto` }}
        />
        <Stack sx={{ flexDirection: 'row', alignItems: 'flex-end' }}>
          <Stack
            sx={{
              width: '70%',
              padding: (theme) => theme.spacing(1),
              paddingBottom: 0,
            }}
          >
            <Text>Square Footage:</Text>
          </Stack>
          <OriginalValue>
            <Text margin={0}>{originalSqft}</Text>
          </OriginalValue>
          <UpdatedValue>
            <Text variant="h5" margin={0} lineHeight="1.5">
              {sqft}
            </Text>
          </UpdatedValue>
        </Stack>
        <HorizontalLine
          sx={{
            margin: (theme) => `${theme.spacing(2.5)} 0 ${theme.spacing(2)} 0`,
          }}
        />
      </Stack>
      <Stack sx={{ flexDirection: 'row', justifyContent: 'end' }}>
        {isMobile && (
          <Stack width="100%">
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirmChanges}
              fullWidth
              sx={{ marginBottom: 1, padding: '12px 22px' }}
            >
              Save changes
            </Button>
            <Button
              variant="outlined"
              color="success"
              onClick={openEditModal}
              fullWidth
              sx={{ padding: '12px 22px' }}
            >
              Go Back
            </Button>
          </Stack>
        )}
        {!isMobile && (
          <>
            <Button
              variant="outlined"
              color="success"
              onClick={openEditModal}
              sx={{ marginRight: (theme) => theme.spacing(1) }}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirmChanges}
            >
              Save changes
            </Button>
          </>
        )}
      </Stack>
    </ConfirmModalContainer>
  );
}
